import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquare, Plus, Clock, UserCheck } from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import type { WhatsAppThread } from "@/types/whatsapp";

interface WhatsAppWidgetProps {
  threads: WhatsAppThread[];
  onOpenChat: (thread: WhatsAppThread) => void;
  onStartChat?: () => void;
  canSend?: boolean;
}

export function WhatsAppWidget({
  threads,
  onOpenChat,
  onStartChat,
  canSend,
}: WhatsAppWidgetProps) {
  const totalUnread = threads.reduce((acc, t) => acc + (t.conversation.unread_count || 0), 0);

  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-[#25D366]/10 text-[#128C7E] dark:text-[#25D366] flex items-center justify-center shrink-0">
            <WhatsAppIcon className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                WhatsApp Conversations ({threads.length})
              </h3>
              {totalUnread > 0 && (
                <Badge className="text-[10px] px-1.5 py-0 bg-[#25D366] text-white font-bold">
                  {totalUnread} new
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Direct chat history and messaging logs
            </p>
          </div>
        </div>

        {canSend && onStartChat && (
          <Button
            type="button"
            size="sm"
            onClick={onStartChat}
            className="gap-1.5 self-start sm:self-center h-8 text-xs font-medium bg-[#25D366] hover:bg-[#128C7E] text-white"
          >
            <Plus className="size-3.5" />
            <span>Start Chat</span>
          </Button>
        )}
      </div>

      {/* List / Cards */}
      {threads.length === 0 ? (
        <div className="p-8 text-center space-y-3">
          <div className="size-10 rounded-full bg-[#25D366]/10 text-[#128C7E] dark:text-[#25D366] flex items-center justify-center mx-auto">
            <WhatsAppIcon className="size-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No WhatsApp chats yet</p>
            <p className="text-xs text-muted-foreground">
              No conversations have been started with this number.
            </p>
          </div>
          {canSend && onStartChat && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onStartChat}
              className="gap-1.5 text-xs border-[#25D366]/40 text-[#128C7E] dark:text-[#25D366]"
            >
              <WhatsAppIcon className="size-3.5" />
              <span>Start WhatsApp Conversation</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {threads.map((thread) => {
            const conv = thread.conversation;
            const messages = thread.messages || [];
            const lastMsg = messages[messages.length - 1];

            return (
              <div
                key={conv.id}
                onClick={() => onOpenChat(thread)}
                className="p-4 hover:bg-muted/40 transition-colors cursor-pointer space-y-2 group"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-xs text-foreground group-hover:text-primary transition-colors">
                      {conv.profile_name ? `${conv.profile_name} (${conv.phone})` : conv.phone}
                    </span>
                    {conv.window_open && (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                        24h Window Open
                      </Badge>
                    )}
                    {conv.unread_count > 0 && (
                      <span className="size-2 rounded-full bg-[#25D366]" />
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-mono shrink-0">
                    {conv.last_message_at && (
                      <span>
                        {new Date(conv.last_message_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <p className="truncate max-w-[80%]">
                    {lastMsg ? (
                      <span>
                        <span className="font-medium text-foreground/70">
                          {lastMsg.direction === "IN" ? "Customer: " : "You: "}
                        </span>
                        {lastMsg.body || lastMsg.caption || `[${lastMsg.message_type}]`}
                      </span>
                    ) : (
                      conv.last_message_preview || "No messages yet"
                    )}
                  </p>

                  {conv.assigned_user && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-foreground/80 font-medium shrink-0">
                      <UserCheck className="size-3 text-primary" />
                      {conv.assigned_user.name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
